import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { UsuarioServiceProvider } from "../usuario-service/usuario-service";
import { catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { ModalController } from '@ionic/angular';
import { ApiService } from "src/app/api.service";
import { ScormPlayerSessionService } from "../scorm-events/scorm-player-session.service";
import { SKIP_JSON_CONTENT_TYPE } from "./http-context.tokens";
import { getPerformanceApiBase } from "../modulo-desempeno/mock/mock-config";
import { recordarDestino } from "../pending-deep-link/pending-deep-link";

@Injectable({
  providedIn: "root",
})
export class InterceptorService implements HttpInterceptor {
  constructor(
    private up: UsuarioServiceProvider,
    private router: Router,
    private modalController: ModalController,
    private api: ApiService,
    private scormSession: ScormPlayerSessionService
  ) {}

  private isReportsApiRequest(url: string): boolean {
    const urlReporte = this.api?.getApiUrls?.urlReporte;
    return !!urlReporte && url.startsWith(urlReporte);
  }

  private isReportsAuthRequest(url: string): boolean {
    const urlReporte = this.api?.getApiUrls?.urlReporte;
    return !!urlReporte && url === `${urlReporte}auth`;
  }

  private isPerformanceApiRequest(url: string): boolean {
    const performanceBase = getPerformanceApiBase();
    return !!performanceBase && url.startsWith(performanceBase);
  }

  /**
   * Cabeceras base de una petición autenticada.
   *
   * En los envíos `multipart/form-data` el `Content-Type` lo tiene que escribir
   * el browser, porque solo él conoce el `boundary`: si lo forzamos a
   * `application/json` el backend recibe un body ilegible. Por eso se omite
   * cuando el body es un `FormData` o cuando el llamador lo pide con
   * `SKIP_JSON_CONTENT_TYPE`.
   */
  private buildAuthHeaders(req: HttpRequest<any>, token: string): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    };

    const skipJson =
      req.body instanceof FormData || req.context.get(SKIP_JSON_CONTENT_TYPE);
    if (!skipJson) {
      headers["Content-Type"] = "application/json";
    }

    return headers;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let request = req;

    if (this.isReportsAuthRequest(req.url)) {
      // reports auth endpoint: no token needed, send as-is
    } else if (this.isReportsApiRequest(req.url)) {
      const reportsToken: string = sessionStorage.getItem('reports_token');
      if (reportsToken) {
        request = req.clone({ setHeaders: this.buildAuthHeaders(req, reportsToken) });
      }
    } else if (this.isPerformanceApiRequest(req.url)) {
      // Plataforma de Desempeño: usa su propio token (obtenido vía
      // platform-login-token). Así, al volver a aprendizaje/inducción, esos
      // request siguen usando el token original porque van a otra base.
      const performanceToken: string = localStorage.getItem("performance_token");
      if (performanceToken) {
        request = req.clone({ setHeaders: this.buildAuthHeaders(req, performanceToken) });
      }
    } else {
      // en el player embebido la sesión vive en memoria; fuera de ahí esto es localStorage
      const token: string = this.scormSession.getItem("token");
      if (token) {
        request = req.clone({ setHeaders: this.buildAuthHeaders(req, token) });
      }
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          // Limpieza involuntaria: un 401 puede llegar incluso sin sesión (los
          // endpoints pre-login del master), así que conservamos la marca activa.
          this.up.clearStorage(true, true, true);
          // Un token revocado que aún no vence: el guard dejó pasar, la API respondió 401.
          // Si se estaba en un enlace retomable, se vuelve a él tras el login.
          recordarDestino(this.router.getCurrentNavigation()?.finalUrl?.toString() ?? this.router.url);
          this.router.navigateByUrl("/login");
        }
        // return throwError(err);
        return throwError(() => err)
      })
    );
  }

  async closeModal(){
    const top = await this.modalController.getTop();
    if (top) await top.dismiss();
  }
}
